import {
  ChangeDetectorRef,
  Component,
  EventEmitter, Input,
  NgZone,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

import { PlanStep } from '../../services/PlanStep';
import { StepComponent } from '../step.component';
import { StrategyMapComponent } from './strategy-map/strategy-map.component';
import { AnnualPlanComponent } from './annual-plan/annual-plan.component';
import { ImplementationPlanComponent } from './implementation-plan/implementation-plan.component';
import { TheoryOfActionComponent } from './theory-of-action/theory-of-action.component';
import { PriorityService } from '../../services/priority.service';
import { MenuItem, Dialog } from 'primeng/primeng';
import { DeliverableService } from '../../services/deliverable.service';
import { IDeliverableHeadings } from '../../services/IDeliverableHeadings';
import { ActivatedRoute } from '@angular/router';
import { IPurposeMissionBeliefs } from '../../services/IPurposeMissionBeliefs';
import { StrategyMapDownloadService,
  DownloadType,
  DownloadConfiguration
} from '../../strategy-map-download/strategy-map-download.service';
import { InitiativeService } from '../../services/initiative.service';
import { PlanCoordinate } from '../../services/plan-coordinate';
import { PlanStepPage } from '../../services/PlanStepPage';
import { IImplementationPlanInitiative } from '../../services/IImplementationPlanInitiative';
import { ITheoryOfActionInitiative } from '../../services/ITheoryOfActionInitiative';
import { AppLoadingService } from '../../../app-loading-panel/app-loading.service';
import { PriorityHeadingsComponent } from './priority-headings/priority-headings.component';
import { WorkspaceService } from '../../services/workspace.service';
import { finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs/index';
import { StrtPriority } from '../../../shared/models/plan/StrtPriority';
import { StrtInitiative } from '../../../shared/models/plan/StrtInitiative';
import {StrategiesDataStoreService} from '../../../shared/services/strategies-data-store.service';
import {ResourceService} from '../../services/resource.service';

@Component({
  selector: 'app-deliverables',
  templateUrl: './deliverables.component.html',
  styleUrls: ['./deliverables.component.scss']
})
export class DeliverablesComponent extends StepComponent<void> implements OnInit, OnChanges, OnDestroy {
  @ViewChild(StrategyMapComponent) strategyMapComp: StrategyMapComponent;
  @ViewChild(AnnualPlanComponent) annualPlanComp: AnnualPlanComponent;
  @ViewChild(ImplementationPlanComponent) implementationPlanComp: ImplementationPlanComponent;
  @ViewChild(TheoryOfActionComponent) theoryOfActionComp: TheoryOfActionComponent;
  @ViewChild(PriorityHeadingsComponent) priorityHeadingsComp: PriorityHeadingsComponent;
  @ViewChild('downloadDialog') downloadDialog: Dialog;


  @Output() planCoordinateChange = new EventEmitter<PlanCoordinate>();

  @Input() strategyId: number;

  private strategyName: string;
  private institutionId: number;
  private _tabCoordinate: PlanCoordinate;

  purposeMissionBeliefs: IPurposeMissionBeliefs = {
    priorities: [],
    purpose: '',
    mission: '',
    beliefs: ''
  };

  priorities: StrtPriority[] = [];
  prioritiesWithInitiatives: StrtPriority[] = [];
  theoryOfActionPrioritiesWithInitiatives: StrtPriority[] = [];
  hasInitiatives = false;
  initiatives: StrtInitiative[] = [];

  subscriptions: Subscription[] = [];

  theoryOfActionSelectedInitiative = InitiativeService.initializeTheoryOfActionInitiative({} as ITheoryOfActionInitiative);
  implementationPlanInitiative = InitiativeService.initializeImplementationPlanInitiative({} as IImplementationPlanInitiative);

  private _headings: IDeliverableHeadings = {keyMeasureHeading: '', initiativeHeading: '', objectiveHeading: ''};
  private readonly _menuItems: MenuItem[] = [];


  constructor(private route: ActivatedRoute,
              private priorityService: PriorityService,
              private deliverableService: DeliverableService,
              private initiativeService: InitiativeService,
              private strategyMapDownloadService: StrategyMapDownloadService,
              private workspaceService: WorkspaceService,
              private cdr: ChangeDetectorRef,
              private appLoadingService: AppLoadingService,
              private ngZone: NgZone,
              private _strtDateStore: StrategiesDataStoreService,
              _resourceService: ResourceService) {
    super(PlanCoordinate.stepBy(PlanStep.OUTPUTS), _resourceService);
  }

  ngOnInit() {
    this.route.data.subscribe(data => {
      this.setActionList();
      this.strategyId = data.strategyId;
      this.fetchHeadings();
      this.strategyName = data.planTree.strategyName;
      this.purposeMissionBeliefs = data.purposeMissionBeliefs;
      this.institutionId = data.purposeMissionBeliefs.institutionId;
    });
    const datedPrioritiesSubscription = this._strtDateStore.datedInitiativesWithActivities$
      .subscribe((priorities: StrtPriority[]) => {
        this.prioritiesWithInitiatives = priorities;
        this.theoryOfActionPrioritiesWithInitiatives = priorities;
      });
    this.subscriptions.push(datedPrioritiesSubscription);
    const prioritySubscription = this._strtDateStore.priorities$.subscribe((priorities: StrtPriority[]) => {
      this.priorities = priorities;
    });

    this.subscriptions.push(prioritySubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  ngOnChanges() {
  }

  get isEditHeadingsActive() {
    return this.priorityHeadingsComp.isActive;
  }

  set isEditHeadingsActive(active) {
    this.priorityHeadingsComp.isActive = active;
  }

  get headings() {
    return this._headings;
  }

  get menuItems() {
    return this._menuItems;
  }

  onClickStrategyMap() {
    if (!this.isStrategyMayActive()) {
      this.setPlanCoordinate(PlanCoordinate.stepPageBy(PlanStepPage.StrategyMap));
    }
  }

  onClickAnnualPlan() {
    if (!this.isAnnualPlanActive()) {
      this.setPlanCoordinate(PlanCoordinate.stepPageBy(PlanStepPage.AnnualPlan));
    }
  }

  onClickImplementationPlan() {
    if (!this.isImplementationPlanActive()) {
      this.setPlanCoordinate(PlanCoordinate.stepPageBy(PlanStepPage.ImplementationPlan));
    }
  }

  onClickTheoryOfAction() {
    if (!this.isTheoryOfActionActive()) {
      this.setPlanCoordinate(PlanCoordinate.stepPageBy(PlanStepPage.TheoryOfAction));
    }
  }

  onPlanCoordinateChange(planCoordinate: PlanCoordinate) {
    this.planCoordinateChange.emit(planCoordinate);
  }

  onHeadingsEdit(headings: IDeliverableHeadings) {
    this.isEditHeadingsActive = false;
    this.deliverableService.updateHeadings(this.strategyId, headings).subscribe(() => {
      this._headings = headings;
    });
  }

  onHeadingsError(error) {
    this.isEditHeadingsActive = false;
    console.error('onHeadingsError', error);
  }

  protected setPlanCoordinate(planCoordinate: PlanCoordinate) {
    super.setPlanCoordinate(planCoordinate);
    this._tabCoordinate = planCoordinate;
    this.fetchHeadings();

    if (this.planCoordinate.isEqual(planCoordinate)) {
      // No plan step page was given, transition to the strategy map
      const preAssign = PlanCoordinate.stepPageBy(PlanStepPage.StrategyMap);
      this._tabCoordinate = preAssign;
      this.onClickStrategyMap();
      return;
    }

    // notify the embedded components directly
    if (this.strategyMapComp) {
      this.strategyMapComp.planCoordinate = planCoordinate;
    }
    if (this.annualPlanComp) {
      this.annualPlanComp.planCoordinate = planCoordinate;
    }
    if (this.implementationPlanComp) {
      this.implementationPlanComp.planCoordinate = planCoordinate;
    }
    if (this.theoryOfActionComp) {
      this.theoryOfActionComp.planCoordinate = planCoordinate;
    }
  }

  protected isSetVisible(planCoordinate: PlanCoordinate) {
    return this.planCoordinate.planDomain === planCoordinate.planDomain && this.planCoordinate.planStep === planCoordinate.planStep;
  }

  private editHeadings() {
    this.isEditHeadingsActive = true;
    this.priorityHeadingsComp.editHeadings(this.headings);
  }

  private downloadDeliverables() {
    this.showModalForm(DownloadType.download);
    this.cdr.detectChanges();
  }

  private shareDeliverables() {
    this.showModalForm(DownloadType.share);
    this.cdr.detectChanges();
  }

  private showModalForm(downloadType: DownloadType): void {
    this.downloadDialog.visible = true;
    const config = this.createDownloadConfig(downloadType);
    this.workspaceService.getWorkspaces(this.strategyId, this.institutionId).subscribe(
      (workspaces) => {
        config.workspaces = workspaces;
        this.strategyMapDownloadService.setVisible(config);
        this.cdr.detectChanges();
      },
      (error) => console.error('showModalForm', error)
    );
  }

  private createDownloadConfig(type: DownloadType): DownloadConfiguration {
    const config = new DownloadConfiguration();
    config.strategyId = this.strategyId;
    config.strategyName = this.strategyName;
    config.downloadType = type;
    config.priorities = this.priorities;
    config.formConfiguration.annualPlansCheckbox = this.annualPlanComp ? true : false;
    config.formConfiguration.annualPlanStartDate =
      this.annualPlanComp ? this.annualPlanComp.startDateField.value : null;
    config.formConfiguration.annualPlanEndDate =
      this.annualPlanComp ? this.annualPlanComp.endDateField.value : null;
    config.formConfiguration.implPlanCheckbox = this.implementationPlanComp ? true : false;
    config.formConfiguration.implPlanStartDate =
      this.implementationPlanComp ? this.implementationPlanComp.startDateField.value : null;
    config.formConfiguration.implPlanEndDate =
      this.implementationPlanComp ? this.implementationPlanComp.endDateField.value : null;
    config.formConfiguration.strategyMapCheckbox = this.strategyMapComp ? true : false;
    config.formConfiguration.theoryOfActionCheckBox = this.theoryOfActionComp ? true : false;
    config.formConfiguration.submitButton = type === DownloadType.download ? 'Download' : 'Next';
    config.formConfiguration.isPlanEmpty = this.priorities.length === 0;
    return config;
  }

  private fetchHeadings() {
    if (!this.strategyId) {
      return;
    }
    this.deliverableService.getHeadings(this.strategyId).subscribe(it => {
      this._headings = it;
      this.cdr.detectChanges();
    });
  }

  private setActionList() {
    this._menuItems.length = 0;
    if (this.permissions.canEditBody) {
      this._menuItems.push({label: '', icon: 'fa fa-pencil', command: () => this.editHeadings()});
    }
    if (this.permissions.canDownload) {
      this._menuItems.push({label: '', icon: 'fa fa-download', command: () => this.downloadDeliverables()});
    }
    if (this.permissions.canView) {
      this._menuItems.push({label: '', icon: 'fa fa-share', command: () => this.shareDeliverables()});
    }
  }

  onRequestInitiatives(data: {startDate?: Date, endDate?: Date}) {
    this.priorityService.getDatedInitiativesWithActivities(this.strategyId, data.startDate, data.endDate);
  }

  onRequestTheoryOfActionInitiatives() {
    setTimeout(() => {
      this.priorityService.getPlanInitiativesWithActivities(this.strategyId);
    });
  }

  onRequestInitiativeDetails(data: {initiativeId: number}) {
    this.appLoadingService.setLoading();
    this.initiativeService.getImplementationPlanInitiative(this.strategyId, data.initiativeId)
      .pipe(finalize(() => this.appLoadingService.clearLoading()))
      .subscribe(result => {
        this.ngZone.run(() => {
          this.implementationPlanInitiative = result;
          this.cdr.detectChanges();
        });
    });
  }

  onRequestAnnualPlanPriorities(data: {startDate: Date, endDate?: Date}) {
    this.priorityService.getAnnualPlanPriorities(this.strategyId, data.startDate, data.endDate);
  }

  onRequestTheoryOfActionInitiativeDetails(data: {initiativeId: number}) {
    this.initiativeService.getTheoryOfActionInitiative(this.strategyId, data.initiativeId)
      .subscribe(result => {
        this.ngZone.run(() => {
          this.theoryOfActionSelectedInitiative = result;
          this.cdr.detectChanges();
        });
    });
  }

  get activeToolbarButtonClass() {
    return 'btn active';
  }

  get inactiveToolbarButtonClass() {
    return 'btn';
  }

  get isStrategyMayActiveClass() {
    return this.isStrategyMayActive()
      ? this.activeToolbarButtonClass : this.inactiveToolbarButtonClass;
  }

  get isAnnualPlanActiveClass() {
    return this.isAnnualPlanActive()
      ? this.activeToolbarButtonClass :
      this.inactiveToolbarButtonClass;
  }

  get isImplementationPlanActiveClass() {
    return this.isImplementationPlanActive()
      ? this.activeToolbarButtonClass :
      this.inactiveToolbarButtonClass;
  }

  get isTheoryOfActionActiveClass() {
    return this.isTheoryOfActionActive()
      ? this.activeToolbarButtonClass :
      this.inactiveToolbarButtonClass;
  }

  private isOutputStepActive(stepPage: PlanStepPage): boolean {
    const leftResult = PlanCoordinate.stepPageBy(stepPage);
    return leftResult.isEqual(this._tabCoordinate);
  }

  isStrategyMayActive() {
    return this.isOutputStepActive(PlanStepPage.StrategyMap);
  }

  isAnnualPlanActive() {
    return this.isOutputStepActive(PlanStepPage.AnnualPlan);
  }

  isImplementationPlanActive() {
    return this.isOutputStepActive(PlanStepPage.ImplementationPlan);
  }

  isTheoryOfActionActive() {
    return this.isOutputStepActive(PlanStepPage.TheoryOfAction);
  }
}
